import React, {useCallback, useMemo, useState} from 'react'
import {MemberVerifyIdentityInfo} from '../../../DTO/MemberVerifyIdentityInfoDTO'
import {useLocation} from 'react-router-dom'
import {MBMemberError} from '../../../Common/MBMemberError'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import {ConfirmRegistrationContent} from './Components/ConfirmRegistrationContent'
import {PasswordInputContent} from '../../Components/InputContentBlocks/PasswordInputContent'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberRepo from '../../../Repo/MemberRepo'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {validateMemberPasswordsInput} from '../../../Utils/Validations/ValidateMembersPasswordInput'
import {animateScroll} from 'react-scroll'
import {ErrorDisplay} from '../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {InputErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/InputErrorSummary'
import {AgreementCheckBoxContentBlock} from '../../Components/InputContentBlocks/AgreementCheckBoxContentBlock'
import {MembersAgreementList} from '../../Ecosystems/MembersAgreement'
import {validateAgreeCheck} from '../../../Utils/Validations/MembersValidation'
import AgreementDTO from '../../../DTO/AgreementDTO'
import {MemberResetPasswordDTO} from '../../../DTO/MemberResetPasswordDTO'

type MemberResetPasswordInputPageProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

type MemberResetPasswordInputPageLocationState = {
  oneTimeToken: string
  memberInfo: MemberVerifyIdentityInfo
}

type State = {
  password: string
  agreedIdList: number[]
}

type ErrorState = {
  isInputError: boolean
  passwordErrors: string[]
  agreeCheckErrors: string[]
}

export const MemberResetPasswordInputPage: React.VFC<MemberResetPasswordInputPageProps> = (
  {navigator, memberRepo},
) => {
  const locationState = useLocation<MemberResetPasswordInputPageLocationState>().state

  const [state, setState] = useState<State>({password: '', agreedIdList: []})
  const [showPasswordChecked, setShowPasswordChecked] = useState(false)
  const [processing, setProcessing] = useState<boolean>(false)
  const [errorState, setErrorState] = useState<ErrorState>({
    isInputError: false, passwordErrors: [], agreeCheckErrors: [],
  })
  const [showAgreement, setShowAgreement] = useState(false)
  const [errorMessages, setErrorMessages] = useState<string[]>([])

  const throwMBMemberError = useMBMemberError()

  const agreementList = useMemo(() => (
    locationState?.memberInfo?.agreementList ?? []).map(json => AgreementDTO.fromJSON(json),
  ), [locationState, locationState?.memberInfo, locationState?.memberInfo?.agreementList])

  const handlePasswordInputChange = useCallback(
    (password: string) => setState(prevState => ({...prevState, password})), [])
  const handleResetPasswordButtonClicked = useCallback(async (): Promise<void> => {
    setShowPasswordChecked(false)
    setProcessing(true)

    const {password, agreedIdList} = state

    const agreeCheckErrors = validateAgreeCheck(agreedIdList)
    const passwordValidateResult = validateMemberPasswordsInput({password: password})
    if (agreeCheckErrors.length !== 0 || passwordValidateResult.hasError) {
      setProcessing(false)
      setErrorState(prevState => ({
        ...prevState,
        isInputError: true,
        passwordErrors: passwordValidateResult.errors.password,
        agreeCheckErrors,
      }))
      animateScroll.scrollToTop()
      return
    }

    try {
      const resultPutResetPassword = await memberRepo.putResetPassword(
        locationState.oneTimeToken,
        new MemberResetPasswordDTO(password, agreedIdList),
      )

      switch (resultPutResetPassword.status) {
        case CommonResponseStatus.Ok:
          navigator.goToResetPasswordCompletePage()
          break
        case CommonResponseStatus.BadRequest:
          setProcessing(false)
          setErrorMessages(resultPutResetPassword.errorMessageList)
          animateScroll.scrollToTop()
          break
        case CommonResponseStatus.NotFound:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('NotFound')
        case CommonResponseStatus.Forbidden:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('InvalidOneTimeUrl')
        case CommonResponseStatus.InternalServerError:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
        default:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('UnknownError')
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'SystemError'
      throwMBMemberError(errorMessage)
    }
  }, [memberRepo, navigator, state.password, state.agreedIdList, locationState.oneTimeToken])

  const handleOpenAgreementButtonClicked = useCallback((): void => {
    if (processing) return
    setShowAgreement(true)
  }, [processing])
  const handleAgreeButtonClicked = useCallback((agreedIdList: number[]): void => {
    setState(prevState => ({...prevState, agreedIdList}))
    setErrorState(prevState => ({...prevState, agreeCheckErrors: validateAgreeCheck(agreedIdList)}))
    setShowAgreement(false)
    window.scrollTo(0, 0)
  }, [])

  if (isInvalidLocationState(locationState)) {
    throw new MBMemberError('InvalidOperation')
  }

  const {password} = state
  const {isInputError, passwordErrors, agreeCheckErrors} = errorState

  return (
    <MembersPageTemplate>
      {showAgreement
        ? <MembersAgreementList
          agreementList={agreementList}
          pageTitle={'利用規約'}
          onAgreeClick={handleAgreeButtonClicked}
          onDisagreeClick={handleAgreeButtonClicked}/>
        : <>
          <PageTitle>パスワード再設定</PageTitle>

          <InputErrorSummary display={isInputError}/>

          <ConfirmRegistrationContent memberInfo={locationState.memberInfo}/>

          <PIMStack alignItems={'center'} spacing={2}>
            <AgreementCheckBoxContentBlock
              agreedIdList={state.agreedIdList}
              agreeCheckErrors={agreeCheckErrors}
              onClick={handleOpenAgreementButtonClicked}
            />

            <PasswordInputContent
              title={'パスワードの再設定'}
              topMessage={'再設定するパスワードを入力してください。'}
              showRequiredBadge={false}
              password={password}
              onChange={handlePasswordInputChange}
              passwordErrors={passwordErrors}
              defaultShowPasswordChecked={showPasswordChecked}
              onCheckboxChange={(checked: boolean): void => setShowPasswordChecked(checked)}
            />
          </PIMStack>

          <MessageForAction>上記の内容に間違いがなければ<br/>『パスワードを再設定する』ボタンを押してください。</MessageForAction>

          <ErrorDisplay errors={errorMessages}/>

          <PIMPageActions>
            <PIMSubmitButton
              testId={'reset-password-button'}
              disabled={processing}
              onClick={(): Promise<void> => handleResetPasswordButtonClicked()}
            >
              パスワードを再設定する
            </PIMSubmitButton>
          </PIMPageActions>
        </>}
    </MembersPageTemplate>
  )
}

const isInvalidLocationState = (
  locationState: MemberResetPasswordInputPageLocationState,
): boolean => !locationState
  || Object.values(locationState).some(value => value === undefined)
  || Object.values(locationState.memberInfo).some(value => value === undefined)
  || locationState.memberInfo.agreementList.length === 0
